import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@carbon/react';
import moment from 'moment';

import Icon from '../../Icon/Icon';
import TimelineMonitor from '../../inputs/Timeline/TimelineMonitor';
import { ChannelDetails, RecordableChannel } from './viewModels';


interface Props {
  channelDetailsById: Record<string, ChannelDetails>;
  channelSelections: Record<string, boolean>;
  onEditSlot: (channelId: string) => void;
  onRecordChannel: (channelId: string) => void;
  onToggleAllChannelSelections: (checked: boolean) => void;
  onToggleChannelSelection: (channelId: string, checked: boolean) => void;
  recordableChannels: RecordableChannel[];
  slotsWithoutActiveConfig: Set<string>;
}

const RecorderListView: React.FC<Props> = ({
  channelDetailsById,
  channelSelections,
  onEditSlot,
  onRecordChannel,
  onToggleAllChannelSelections,
  onToggleChannelSelection,
  recordableChannels,
  slotsWithoutActiveConfig,
}) => {
  const allChannelsSelected =
    recordableChannels.filter((channel) => channel.enabled).length > 0 &&
    recordableChannels
      .filter((channel) => channel.enabled)
      .every((channel) => channelSelections[channel.id] ?? false);

  return (
    <TableContainer title="">
      <Table size="sm">
      <colgroup>
        <col style={{ width: 30 }} />
        <col style={{ width: 80 }} />
        <col />
        <col style={{ width: 120 }} />
        <col style={{ width: 120 }} />
        <col style={{ width: 170 }} />
        <col style={{ width: 170 }} />
        <col style={{ width: 72 }} />
      </colgroup>
      <TableHead>
        <TableRow>
          <TableHeader>
            <input
              type="checkbox"
              checked={allChannelsSelected}
              onChange={(event) => onToggleAllChannelSelections(event.target.checked)}
            />
          </TableHeader>
          <TableHeader />
          <TableHeader>Name</TableHeader>
          <TableHeader>Signal</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Start</TableHeader>
          <TableHeader>End</TableHeader>
          <TableHeader />
        </TableRow>
      </TableHead>
      <TableBody>
        {recordableChannels.map((channel) => {
          const details = channelDetailsById[channel.id];
          const latestItem = details?.latestItem;

          const missingConfig = slotsWithoutActiveConfig.has(channel.id);
          const isDisabled = channel.enabled === false;
          const cannotRecord = missingConfig || isDisabled;

          return (
            <TableRow
              key={`list_channel_${channel.id}`}
              style={{
                ...(missingConfig ? { borderLeft: '3px solid #f1c21b' } : {}),
                ...(isDisabled ? { opacity: 0.6 } : {}),
              }}
            >
              <TableCell>
                <input
                  type="checkbox"
                  checked={channelSelections[channel.id] ?? false}
                  disabled={isDisabled}
                  onChange={(event) => onToggleChannelSelection(channel.id, event.target.checked)}
                />
              </TableCell>
              <TableCell>
                <div
                  style={{
                    width: 80,
                    height: 45,
                    backgroundColor: '#222',
                    border: '1px solid #333',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  {channel.monitorPreviewURL ? (
                    <TimelineMonitor
                      src={channel.monitorPreviewURL}
                      type={channel.monitorPreviewType}
                    />
                  ) : null}
                </div>
              </TableCell>
              <TableCell>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {channel.title}
                  {isDisabled && (
                    <span title="Slot disabled">
                      <Icon type="control_pause" color="#8d8d8d" size={14} />
                    </span>
                  )}
                  {missingConfig && (
                    <span title="No default config — cannot record">
                      <Icon type="warning" color="#f1c21b" size={14} />
                    </span>
                  )}
                </span>
              </TableCell>
              <TableCell>{details?.signal ?? '-'}</TableCell>
              <TableCell>{details?.status ?? 'Stand-by'}</TableCell>
              <TableCell>{latestItem?.start ? moment(latestItem.start).format('DD.MM.YYYY | HH:mm') : '-'}</TableCell>
              <TableCell>{latestItem?.end ? moment(latestItem.end).format('DD.MM.YYYY | HH:mm') : '-'}</TableCell>
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                  <button
                    type="button"
                    title="Record now"
                    disabled={cannotRecord}
                    onClick={() => onRecordChannel(channel.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: cannotRecord ? 'not-allowed' : 'pointer',
                      padding: 4,
                      display: 'flex',
                      alignItems: 'center',
                      opacity: cannotRecord ? 0.4 : 1,
                    }}
                  >
                    <Icon type="circle" color="#da1e28" size={16} />
                  </button>
                  <button
                    type="button"
                    title="Edit slot"
                    onClick={() => onEditSlot(channel.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
                  >
                    <Icon type="settings" color="#8d8d8d" size={16} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
        {recordableChannels.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9}>No channels available.</TableCell>
          </TableRow>
        ) : null}
      </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RecorderListView;
